import { administration } from './support'
import ISystemStatusMessage, {
  ISystemStatusMessageInput
} from '../model/ISystemStatusMessage'
import { normalizeMessage } from '../utils/systemStatus'

// Global (feature-less) system status messages — V4 FeatureController.
// GET requires scope platform_iam_featurestatus_read; writes additionally
// require platform_iam_featurestatus_readwrite + the BackOffice role.
const BASE_URL = 'admin/feature/message'
const VERSION = 4

// MessageExpiryFilter values the backend accepts (bound by name).
export type ExpiryFilter = 'All' | 'NonExpired' | 'Expired' | 'Pending'

interface CursorPage {
  cursor: string | null
  data: ISystemStatusMessage[]
}

// The backend list is cursor-paginated. The global set is small, so we follow
// the cursor to load every message and let the UI paginate client-side.
// `NonExpired` asks the server for currently-active messages (banner feed);
// `All` returns everything (BackOffice table).
export const getSystemStatusMessages = async (
  expiry: ExpiryFilter = 'All'
): Promise<ISystemStatusMessage[]> => {
  const all: ISystemStatusMessage[] = []
  let cursor: string | null = null
  do {
    const query = new URLSearchParams({ expiry })
    if (cursor) {
      query.set('cursor', cursor)
    }
    const page: CursorPage = await administration.getJson(
      `${BASE_URL}?${query.toString()}`,
      VERSION
    )
    all.push(...(page?.data ?? []).map(normalizeMessage))
    cursor = page?.cursor ?? null
  } while (cursor)
  return all
}

// Per-feature listing for the banner: returns this feature's messages PLUS all
// global ones. Any valid user with platform_iam_featurestatus_read may call it
// for their own feature (backend enforces client_id === featureId), so it does
// NOT require the BackOffice role that the global list above does.
export const getFeatureStatusMessages = async (
  featureId: string,
  expiry: ExpiryFilter = 'NonExpired'
): Promise<ISystemStatusMessage[]> => {
  const all: ISystemStatusMessage[] = []
  let cursor: string | null = null
  do {
    const query = new URLSearchParams({ expiry })
    if (cursor) {
      query.set('cursor', cursor)
    }
    const page: CursorPage = await administration.getJson(
      `admin/feature/${encodeURIComponent(
        featureId
      )}/message?${query.toString()}`,
      VERSION
    )
    all.push(...(page?.data ?? []).map(normalizeMessage))
    cursor = page?.cursor ?? null
  } while (cursor)
  return all
}

export const createSystemStatusMessage = (
  input: ISystemStatusMessageInput
): Promise<ISystemStatusMessage> =>
  administration.postJson(BASE_URL, input, VERSION)

export const updateSystemStatusMessage = (
  id: number,
  input: ISystemStatusMessageInput
): Promise<ISystemStatusMessage> =>
  administration.patchJson(`${BASE_URL}/${id}`, input, VERSION)

export const deleteSystemStatusMessage = (id: number): Promise<void> =>
  administration.delJson(`${BASE_URL}/${id}`, VERSION)
